import { useCallback } from 'react'
import type React from 'react'
import type { CanvasToolbarSide } from '../utils/canvasToolbarSide'
import {
  useMagnetDrag,
  MAGNET_DRAG_THRESHOLD,
  MAGNET_SETTLE_MS,
  type MagnetDragPhase,
} from '../../../hooks/useMagnetDrag'

/** Ab so vielen Pixeln Bewegung ist ein Druck auf den Griff ein Zug. */
export const TOOLBAR_DRAG_THRESHOLD = MAGNET_DRAG_THRESHOLD

/** Wie lange die Pille ins Ziel gleitet — `ProjectCanvasPanel.scss` muss dazu passen. */
export const TOOLBAR_SETTLE_MS = MAGNET_SETTLE_MS

export interface ToolbarDragGeometry {
  /** Die Pille selbst, in Client-Koordinaten. */
  pill: DOMRect
  /** Die Ableg-Fläche am Ursprung — die Platte. */
  origin: DOMRect
  /** Die Ableg-Fläche auf der Gegenseite. */
  target: DOMRect
}

export type ToolbarDragPhase = MagnetDragPhase
export type ToolbarDragNearest = 'origin' | 'target'

interface UseCanvasToolbarDragOptions {
  side: CanvasToolbarSide
  pillRef: React.RefObject<HTMLElement | null>
  /**
   * Misst die drei Rechtecke beim Zugbeginn. `null` = kein Zug (die
   * Gegen-Fläche ist noch nicht im DOM, das Panel schließt gerade).
   */
  measure: () => ToolbarDragGeometry | null
  /** Die Pille ist auf der Gegenseite gelandet. */
  onSideChange: (side: CanvasToolbarSide) => void
}

export interface CanvasToolbarDragHandle {
  gripPointerDown: (event: React.PointerEvent) => void
  phase: ToolbarDragPhase
  /** Welche Ableg-Fläche sich gerade meldet — nur während des Zugs. */
  nearest: ToolbarDragNearest | null
}

const other = (side: CanvasToolbarSide): CanvasToolbarSide => (side === 'left' ? 'right' : 'left')

/**
 * Der Zug der **Werkzeug-Pille** am **Griff** auf die andere Seite (CONTEXT.md,
 * Werkzeug-Pille) — zwei Ziele für `useMagnetDrag`: der Ursprung und sein
 * Spiegelbild. Die nähere Fläche gewinnt, die Höhe zählt nicht.
 *
 * @example
 * const drag = useCanvasToolbarDrag({ side, pillRef, measure, onSideChange: setCanvasToolbarSide })
 * <CanvasToolbar onGripPointerDown={drag.gripPointerDown} gripPressed={drag.phase === 'dragging'} />
 */
export function useCanvasToolbarDrag({
  side, pillRef, measure, onSideChange,
}: UseCanvasToolbarDragOptions): CanvasToolbarDragHandle {
  const measureTargets = useCallback(() => {
    const geometry = measure()
    return geometry ? { pill: geometry.pill, targets: { origin: geometry.origin, target: geometry.target } } : null
  }, [measure])

  const handleChange = useCallback((next: ToolbarDragNearest) => {
    if (next === 'target') onSideChange(other(side))
  }, [onSideChange, side])

  return useMagnetDrag<ToolbarDragNearest>({
    current: 'origin',
    pillRef,
    measure: measureTargets,
    onChange: handleChange,
  })
}
